/**
 * Copied from slate-md-serializer so that the editor can serialize and deserialize properly
 */
import generateHashtagRegex from 'hashtag-regex'

const hashtag = new RegExp(generateHashtagRegex().source.replace(/^/, '^(').replace(/$/, ')'))

const EMPTY_PARAGRAPH_NODES = [
  {
    object: 'text',
    leaves: [
      {
        object: 'leaf',
        text: '',
        marks: []
      }
    ]
  }
]

const hasOwnProperty = Object.prototype.hasOwnProperty

const assign =
  Object.assign ||
  function (obj: any) {
    let i = 1
    for (; i < arguments.length; i++) {
      // eslint-disable-next-line prefer-rest-params
      const target = arguments[i]
      for (const key in target) {
        if (hasOwnProperty.call(target, key)) {
          obj[key] = target[key]
        }
      }
    }
    return obj
  }

const flatten = function (ary: any) {
  return ary.flat()
}

const noop = function () {}
noop.exec = noop

const defaults = {
  gfm: true,
  tables: true,
  breaks: false,
  pedantic: false,
  smartLists: true,
  silent: false,
  // @ts-expect-error
  renderer: new Renderer()
}

/**
 * Block-Level Grammar
 */

const block: Record<string, RegExp | any> = {
  newline: /^\n+/,
  code: /^( {4}[^\n]+\n*)+/,
  fences: noop,
  hr: /^( *[*_-]){3,} *(?:\n|$)/,
  heading: /^ *(#{1,6}) *([^\n]+?)? *#* *(?:\n|$)/,
  nptable: noop,
  blockquote: /^( *>[^\n]+(\n(?!def)[^\n])*(?:\n|$))+/,
  list: /^( *)(bull) [\S\s]+?(?:hr|def|\n(?! )(?!\1bull )\n|\s*$)/,
  def: /^ *\[([^\]]+)]: *<?([^\s>]+)>?(?: +["(]([^\n]+)[")])? *(?:\n|$)/,
  paragraph: /^((?:[^\n]+(?!hr|heading|blockquote|def))+)(?:\n|$)/,
  text: /^[^\n]+/
}

block.bullet = /[*+-]|\d+\.|\[[\sx]]/
block.item = /^( *)(bull) [^\n]*(?:\n(?!\1bull )[^\n]*)*/
// @ts-expect-error
block.item = replace(block.item, 'gm')(/bull/g, block.bullet)()

// @ts-expect-error
block.list = replace(block.list)(/bull/g, block.bullet)('hr', '\\n+(?=\\1?(?:[-*_] *){3,}(?:\\n+|$))')(
  'def',
  '\\n+(?=' + block.def.source + ')'
)()

// @ts-expect-error
block.blockquote = replace(block.blockquote)('def', block.def)()

// @ts-expect-error
block.paragraph = replace(block.paragraph)('hr', block.hr)('heading', block.heading)('blockquote', block.blockquote)(
  'def',
  block.def
)()

/**
 * Normal Block Grammar
 */

block.normal = assign({}, block)

/**
 * GFM Block Grammar
 */

block.gfm = assign({}, block.normal, {
  fences: /^ *(`{3,}|~{3,})[ .]*(\S+)? *\n([\S\s]+?)\s*\1 *(?:\n|$)/,
  paragraph: /^/,
  heading: /^ *(#{1,6}) +([^\n]+?)? *#* *(?:\n{1,2}|$)/
})

// @ts-expect-error
block.gfm.paragraph = replace(block.paragraph)(
  '(?!',
  '(?!' + block.gfm.fences.source.replace('\\1', '\\2') + '|' + block.list.source.replace('\\1', '\\3') + '|'
)()

/**
 * GFM + Tables Block Grammar
 */

block.tables = assign({}, block.gfm, {
  nptable: /^ *(\S.*\|.*)\n *([:-]+ *\|[ :|-]*)\n((?:.*\|.*(?:\n|$))*)/,
  table: /^ *\|(.+)\n *\|( *[:-]+[ :|-]*)\n((?: *\|.*(?:\n|$))*)/
})

/**
 * Block Lexer
 */

function Lexer(this: any, options: any) {
  this.tokens = []
  this.tokens.links = {}
  this.options = assign({}, options || defaults)
  this.rules = block.normal

  if (this.options.gfm) {
    if (this.options.tables) {
      this.rules = block.tables
    } else {
      this.rules = block.gfm
    }
  }
}

/**
 * Expose Block Rules
 */

Lexer.rules = block

/**
 * Static Lex Method
 */

Lexer.parse = function (src: any, options: any) {
  // @ts-expect-error
  const lexer = new Lexer(options)
  return lexer.parse(src)
}

/**
 * Preprocessing
 */

Lexer.prototype.parse = function (src: any) {
  src = src
    .replaceAll(/\r\n|\r/g, '\n')
    .replaceAll('\t', '    ')
    .replaceAll('\u00A0', ' ')
    .replaceAll('␤', '\n')

  return this.token(src, true)
}

/**
 * Lexing
 */

Lexer.prototype.token = function (src: any, top: any, bq: any) {
  let next
  let loose
  let cap
  let bull
  let b
  let item
  let space
  let i
  let l

  src = src.replaceAll(/^ +$/gm, '')
  src = src.replace(/^\n/, '')

  while (src) {
    // newline
    if ((cap = this.rules.newline.exec(src))) {
      src = src.slice(cap[0].length)
      const newlines = cap[0].length

      if (top) {
        for (let i = 0; i < newlines; i++) {
          this.tokens.push({
            type: 'paragraph',
            text: ''
          })
        }
      }
    }

    // code
    if ((cap = this.rules.code.exec(src))) {
      src = src.slice(cap[0].length)
      cap = cap[0].replaceAll(/^ {4}/gm, '')
      this.tokens.push({
        type: 'code',
        text: this.options.pedantic ? cap : cap.replace(/\n+$/, '')
      })
      continue
    }

    // fences (gfm)
    if ((cap = this.rules.fences.exec(src))) {
      src = src.slice(cap[0].length)
      this.tokens.push({
        type: 'code',
        lang: cap[2],
        text: cap[3]
      })
      continue
    }

    // heading
    if ((cap = this.rules.heading.exec(src))) {
      src = src.slice(cap[0].length)

      const last = this.tokens.at(-1)
      if (last && last.type === 'paragraph' && last.text === '') {
        this.tokens.splice(-1, 1)
      }

      this.tokens.push({
        type: 'heading',
        depth: cap[1].length,
        text: cap[2]
      })
      continue
    }

    // table no leading pipe (gfm)
    if (top && (cap = this.rules.nptable.exec(src))) {
      src = src.slice(cap[0].length)

      item = {
        type: 'table',
        header: splitCells(cap[1].replaceAll(/^ *| *\| *$/g, '')),
        align: cap[2].replaceAll(/^ *|\| *$/g, '').split(/ *\| */),
        cells: cap[3].replace(/\n$/, '').split('\n')
      }

      for (i = 0; i < item.align.length; i++) {
        if (/^ *-+: *$/.test(item.align[i])) {
          item.align[i] = 'right'
        } else if (/^ *:-+: *$/.test(item.align[i])) {
          item.align[i] = 'center'
        } else if (/^ *:-+ *$/.test(item.align[i])) {
          item.align[i] = 'left'
        } else {
          item.align[i] = null
        }
      }

      for (i = 0; i < item.cells.length; i++) {
        item.cells[i] = splitCells(item.cells[i])
      }

      this.tokens.push(item)

      continue
    }

    // hr
    if ((cap = this.rules.hr.exec(src))) {
      src = src.slice(cap[0].length)
      this.tokens.push({
        type: 'hr'
      })
      continue
    }

    // blockquote
    if ((cap = this.rules.blockquote.exec(src))) {
      src = src.slice(cap[0].length)

      this.tokens.push({
        type: 'blockquote_start'
      })

      cap = cap[0].replaceAll(/^ *> ?/gm, '')

      // Pass `top` to keep the current
      // "toplevel" state. This is exactly
      // how markdown.pl works.
      this.token(cap, top, true)

      this.tokens.push({
        type: 'blockquote_end'
      })

      continue
    }

    // list
    if ((cap = this.rules.list.exec(src))) {
      src = src.slice(cap[0].length)
      bull = cap[2]
      const ordered = bull.length > 1
      const todo = bull[0] === '['

      this.tokens.push({
        type: 'list_start',
        style: todo ? 'todo' : ordered ? 'ordered' : 'bulleted'
      })

      // Get each top-level item.
      cap = cap[0].match(this.rules.item)

      next = false
      l = cap.length
      i = 0

      for (; i < l; i++) {
        item = cap[i]

        // Remove the list item's bullet
        // so it is seen as the next token.
        const checked = todo ? !!/^ *(\[x])/.test(item) : undefined
        space = item.length
        item = item.replace(/^ *([*+-]|\d+\.|\[[\sx]]) +/, '')

        // Outdent whatever the
        // list item contains. Hacky.
        if (~item.indexOf('\n ')) {
          space -= item.length
          item = this.options.pedantic
            ? item.replaceAll(/^ {1,4}/gm, '')
            : item.replaceAll(new RegExp('^ {1,' + space + '}', 'gm'), '')
        }

        // Determine whether the next list item belongs here.
        // Backpedal if it does not belong in this list.
        if (this.options.smartLists && i !== l - 1) {
          b = block.bullet.exec(cap[i + 1])[0]
          if (bull !== b && !(bull.length > 1 && b.length > 1)) {
            src = cap.slice(i + 1).join('\n') + src
            i = l - 1
          }
        }

        // Determine whether item is loose or not.
        // Use: /(^|\n)(?! )[^\n]+\n\n(?!\s*$)/
        // for discount behavior.
        loose = next || /\n\n(?!\s*$)/.test(item)
        if (i !== l - 1) {
          // eslint-disable-next-line unicorn/prefer-at
          next = item.charAt(item.length - 1) === '\n'
          if (!loose) {
            loose = next
          }
        }

        this.tokens.push({
          checked,
          type: loose ? 'loose_item_start' : 'list_item_start'
        })

        // Recurse.
        this.token(item, false, bq)

        this.tokens.push({
          type: 'list_item_end'
        })
      }

      this.tokens.push({
        type: 'list_end'
      })

      continue
    }

    // def
    if (!bq && top && (cap = this.rules.def.exec(src))) {
      src = src.slice(cap[0].length)
      this.tokens.links[cap[1].toLowerCase()] = {
        href: cap[2],
        title: cap[3]
      }
      continue
    }

    // table (gfm)
    if (top && (cap = this.rules.table.exec(src))) {
      src = src.slice(cap[0].length)

      item = {
        type: 'table',
        header: splitCells(cap[1].replaceAll(/^ *| *\| *$/g, '')),
        align: cap[2].replaceAll(/^ *|\| *$/g, '').split(/ *\| */),
        cells: cap[3].replace(/(?: *\| *)?\n$/, '').split('\n')
      }

      for (i = 0; i < item.align.length; i++) {
        if (/^ *-+: *$/.test(item.align[i])) {
          item.align[i] = 'right'
        } else if (/^ *:-+: *$/.test(item.align[i])) {
          item.align[i] = 'center'
        } else if (/^ *:-+ *$/.test(item.align[i])) {
          item.align[i] = 'left'
        } else {
          item.align[i] = null
        }
      }

      for (i = 0; i < item.cells.length; i++) {
        item.cells[i] = splitCells(item.cells[i].replaceAll(/^ *\| *| *\| *$/g, ''))
      }

      this.tokens.push(item)

      continue
    }

    // top-level paragraph
    if (top && (cap = this.rules.paragraph.exec(src))) {
      src = src.slice(cap[0].length)
      // eslint-disable-next-line unicorn/prefer-at
      const endsWithNewline = cap[1].charAt(cap[1].length - 1) === '\n'
      this.tokens.push({
        type: 'paragraph',
        text: endsWithNewline ? cap[1].slice(0, -1) : cap[1]
      })
      if (endsWithNewline) {
        this.tokens.push({
          type: 'paragraph',
          text: ''
        })
      }
      continue
    }

    // text
    if ((cap = this.rules.text.exec(src))) {
      // Top-level should never reach here.
      src = src.slice(cap[0].length)
      this.tokens.push({
        type: 'text',
        text: cap[0]
      })
      continue
    }

    if (src) {
      // eslint-disable-next-line unicorn/prefer-code-point
      throw new Error('Infinite loop on byte: ' + src.charCodeAt(0))
    }
  }

  return this.tokens
}

/**
 * Inline-Level Grammar
 */

const inline: Record<string, RegExp | any> = {
  escape: /^\\([!#()*+.>[\\\]_`{}-])/,
  link: /^!?\[(inside)]\(href\)/,
  hashtag,
  reflink: /^!?\[(inside)]\s*\[([^\]]*)]/,
  nolink: /^!?\[((?:\[[^\]]*]|[^[\]])*)]/,
  strong: /^\*\*([\S\s]+?)\*\*(?!\*)/,
  underlined: /^__([\S\s]+?)__(?!_)/,
  em: /^\b_((?:__|[\S\s])+?)_\b|^\*((?:\*\*|[\S\s])+?)\*(?!\*)/,
  code: /^(`+)\s*([\S\s]*?[^`])\s*\1(?!`)/,
  br: /^ {2,}\n(?!\s*$)/,
  del: noop,
  ins: noop,
  text: /^[\S\s]+?(?=[!#*<[\\_`]| {2,}\n|$)/
}

inline._inside = /(?:\[[^\]]*]|[^[\]]|](?=[^[]*]))*/
inline._href = /\s*<?([\S\s]*?)>?(?:\s+["']([\S\s]*?)["'])?\s*/

// @ts-expect-error
inline.link = replace(inline.link)('inside', inline._inside)('href', inline._href)()

// @ts-expect-error
inline.reflink = replace(inline.reflink)('inside', inline._inside)()

/**
 * Normal Inline Grammar
 */

inline.normal = assign({}, inline)

/**
 * Pedantic Inline Grammar
 */

inline.pedantic = assign({}, inline.normal, {
  strong: /^__(?=\S)([\S\s]*?\S)__(?!_)|^\*\*(?=\S)([\S\s]*?\S)\*\*(?!\*)/,
  em: /^_(?=\S)([\S\s]*?\S)_(?!_)|^\*(?=\S)([\S\s]*?\S)\*(?!\*)/
})

/**
 * GFM Inline Grammar
 */

inline.gfm = assign({}, inline.normal, {
  // @ts-expect-error
  escape: replace(inline.escape)('])', '~|])')(),
  del: /^~~(?=\S)([\S\s]*?\S)~~/,
  ins: /^\+\+(?=\S)([\S\s]*?\S)\+\+/,
  // @ts-expect-error
  text: replace(inline.text)(']|', '~+]|')()
})

/**
 * GFM + Line Breaks Inline Grammar
 */

inline.breaks = assign({}, inline.gfm, {
  // @ts-expect-error
  br: replace(inline.br)('{2,}', '*')(),
  // @ts-expect-error
  text: replace(inline.gfm.text)('{2,}', '*')()
})

/**
 * Inline Lexer & Compiler
 */

function InlineLexer(this: any, links: any, options: any) {
  this.options = assign({}, options || defaults)
  this.links = links
  this.rules = inline.normal
  // @ts-expect-error
  this.renderer = this.options.renderer || new Renderer()
  this.renderer.options = this.options

  if (!this.links) {
    throw new Error('Tokens array requires a `links` property.')
  }

  if (this.options.gfm) {
    if (this.options.breaks) {
      this.rules = inline.breaks
    } else {
      this.rules = inline.gfm
    }
  } else if (this.options.pedantic) {
    this.rules = inline.pedantic
  }
}

/**
 * Expose Inline Rules
 */

InlineLexer.rules = inline

/**
 * Static Lexing/Compiling Method
 */

InlineLexer.parse = function (src: any, links: any, options: any) {
  // @ts-expect-error
  const inline = new InlineLexer(links, options)
  return inline.parse(src)
}

/**
 * Lexing/Compiling
 */

InlineLexer.prototype.parse = function (src: any) {
  const out = []
  let link
  let cap

  while (src) {
    // escape
    if ((cap = this.rules.escape.exec(src))) {
      src = src.slice(cap[0].length)
      out.push({
        object: 'text',
        leaves: [
          {
            text: cap[1]
          }
        ]
      })
      continue
    }

    // link
    if ((cap = this.rules.link.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.outputLink(cap, { href: cap[2], title: cap[3] }))
      continue
    }

    // hashtag
    if ((cap = this.rules.hashtag.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.hashtag(this.parse(`\\${cap[1]}`)))
      continue
    }

    if ((cap = this.rules.reflink.exec(src)) || (cap = this.rules.nolink.exec(src))) {
      src = src.slice(cap[0].length)
      link = (cap[2] || cap[1]).replaceAll(/\s+/g, ' ')
      link = this.links[link.toLowerCase()]
      if (!link || !link.href) {
        out.push({
          object: 'text',
          leaves: [
            {
              text: cap[0].charAt(0)
            }
          ]
        })
        src = cap[0].slice(1) + src
        continue
      }
      out.push(this.outputLink(cap, link))
      continue
    }

    // underlined
    if ((cap = this.rules.underlined.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.underlined(this.parse(cap[2] || cap[1])))
      continue
    }

    // strong
    if ((cap = this.rules.strong.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.strong(this.parse(cap[2] || cap[1])))
      continue
    }

    // em
    if ((cap = this.rules.em.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.em(this.parse(cap[2] || cap[1])))
      continue
    }

    // code
    if ((cap = this.rules.code.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.codespan(cap[2]))
      continue
    }

    // br
    if ((cap = this.rules.br.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.br())
      continue
    }

    // del (gfm)
    if ((cap = this.rules.del.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.del(this.parse(cap[1])))
      continue
    }

    // ins (gfm extended)
    if ((cap = this.rules.ins.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.ins(this.parse(cap[1])))
      continue
    }

    // text
    if ((cap = this.rules.text.exec(src))) {
      src = src.slice(cap[0].length)
      out.push(this.renderer.text(cap[0]))
      continue
    }

    if (src) {
      // eslint-disable-next-line unicorn/prefer-code-point
      throw new Error('Infinite loop on byte: ' + src.charCodeAt(0))
    }
  }

  return out
}

/**
 * Compile Link
 */

InlineLexer.prototype.outputLink = function (cap: any, link: any) {
  const href = link.href
  const title = link.title

  return cap[0].charAt(0) === '!'
    ? this.renderer.image(href, title, cap[1])
    : this.renderer.link(href, title, this.parse(cap[1]))
}

/**
 * Renderer
 */

function Renderer(this: any, options: any) {
  this.options = options || {}
}

Renderer.prototype.groupTextInLeaves = function (childNode: any) {
  const node: any[] = flatten(childNode)
  const output = node.reduce((acc, current) => {
    const accLast = acc.length - 1
    const lastIsText = accLast >= 0 && acc[accLast] && acc[accLast]['object'] === 'text'

    if (current.text) {
      if (lastIsText) {
        // If the previous item was a text object, push the current text to it's range
        acc[accLast].leaves.push(current)
        return acc
      } else {
        // Else, create a new text object
        acc.push({
          object: 'text',
          leaves: [current]
        })
        return acc
      }
    } else if (Array.isArray(current)) {
      return acc.concat(this.groupTextInLeaves(current))
    } else {
      acc.push(current)
      return acc
    }
  }, [])

  if (output.length === 0) return EMPTY_PARAGRAPH_NODES
  return output
}

Renderer.prototype.code = function (childNode: any, language: any) {
  const data: { language?: any } = {}

  if (language) {
    data.language = language
  }

  return {
    object: 'block',
    type: 'code',
    data,
    nodes: this.groupTextInLeaves(childNode)
  }
}

Renderer.prototype.blockquote = function (childNode: any) {
  return {
    object: 'block',
    type: 'block-quote',
    nodes: this.groupTextInLeaves(childNode)
  }
}

Renderer.prototype.heading = function (childNode: any, level: any) {
  return {
    object: 'block',
    type: 'heading' + level,
    nodes: this.groupTextInLeaves(childNode)
  }
}

Renderer.prototype.hr = function () {
  return {
    object: 'block',
    type: 'horizontal-rule',
    isVoid: true,
    nodes: EMPTY_PARAGRAPH_NODES
  }
}

Renderer.prototype.list = function (childNode: any, style: any) {
  return {
    object: 'block',
    type: `${style}-list`,
    nodes: childNode
  }
}

Renderer.prototype.listitem = function (childNode: any, flags: any = {}) {
  let data
  if (flags.checked !== undefined) {
    data = { checked: flags.checked }
  }

  return {
    object: 'block',
    type: 'list-item',
    data,
    nodes: this.groupTextInLeaves(childNode)
  }
}

Renderer.prototype.paragraph = function (childNode: any) {
  return {
    object: 'block',
    type: 'paragraph',
    nodes: this.groupTextInLeaves(childNode)
  }
}

Renderer.prototype.table = function (childNode: any) {
  return {
    object: 'block',
    type: 'table',
    nodes: childNode
  }
}

Renderer.prototype.tablerow = function (childNode: any) {
  return {
    object: 'block',
    type: 'table-row',
    nodes: childNode
  }
}

Renderer.prototype.tablecell = function (childNode: any, flags: any) {
  const align = flags.align

  return {
    object: 'block',
    data: { align },
    type: 'table-cell',
    nodes: [this.paragraph(childNode)]
  }
}

function applyMark(childNode: any, type: any) {
  return childNode.map((node: any) => {
    if (node.object === 'inline') {
      node.nodes = applyMark(node.nodes, type)
    } else if (node.object === 'text') {
      node.leaves = applyMark(node.leaves, type)
    } else if (node.marks) {
      node.marks.push({ type })
    } else {
      node.marks = [{ type }]
    }
    return node
  })
}

// span level renderer
Renderer.prototype.underlined = function (childNode: any) {
  return applyMark(childNode, 'underlined')
}

Renderer.prototype.strong = function (childNode: any) {
  return applyMark(childNode, 'bold')
}

Renderer.prototype.em = function (childNode: any) {
  return applyMark(childNode, 'italic')
}

Renderer.prototype.codespan = function (text: any) {
  return {
    text,
    marks: [{ type: 'code' }]
  }
}

Renderer.prototype.br = function () {
  return {
    text: ' '
  }
}

Renderer.prototype.del = function (childNode: any) {
  return applyMark(childNode, 'deleted')
}

Renderer.prototype.ins = function (childNode: any) {
  return applyMark(childNode, 'inserted')
}

Renderer.prototype.hashtag = function (childNode: any) {
  return {
    object: 'inline',
    type: 'hashtag',
    nodes: this.groupTextInLeaves(childNode)
  }
}

Renderer.prototype.link = function (href: any, title: any, childNode: any) {
  const data: { href: any; title?: any } = {
    //Changed this line not to decode the url
    //src:decode(href)
    href: href
  }
  if (title) {
    data.title = title
  }
  return {
    object: 'inline',
    type: 'link',
    nodes: this.groupTextInLeaves(childNode),
    data: data
  }
}

Renderer.prototype.image = function (href: any, title: any, alt: any) {
  const data: { src: any; title?: any; alt?: any } = {
    //Changed this line not to decode the url
    //src:decode(href)
    src: href
  }

  if (title) {
    data.title = title
  }
  if (alt) {
    data.alt = alt
  }

  return {
    object: 'block',
    type: 'image',
    nodes: EMPTY_PARAGRAPH_NODES,
    isVoid: true,
    data: data
  }
}

Renderer.prototype.text = function (childNode: any) {
  return {
    text: childNode
  }
}

/**
 * Parsing & Compiling
 */

function Parser(this: any, options: any) {
  this.tokens = []
  this.token = null
  this.options = assign({}, options || defaults)
  // @ts-expect-error
  this.options.renderer = this.options.renderer || new Renderer()
  this.renderer = this.options.renderer
  this.renderer.options = this.options
}

/**
 * Static Parse Method
 */

Parser.parse = function (src: any, options: any) {
  // @ts-expect-error
  const parser = new Parser(options)
  return parser.parse(src)
}

/**
 * Parse Loop
 */

Parser.prototype.parse = function (src: any) {
  // @ts-expect-error
  this.inline = new InlineLexer(src.links, this.options, this.renderer)
  this.tokens = src.slice().reverse()

  const out = []
  while (this.next()) {
    out.push(this.tok())
  }

  return out
}

/**
 * Next Token
 */

Parser.prototype.next = function () {
  return (this.token = this.tokens.pop())
}

/**
 * Preview Next Token
 */

Parser.prototype.peek = function () {
  return this.tokens.at(-1) || 0
}

/**
 * Parse Text Tokens
 */

Parser.prototype.parseText = function () {
  let body = this.token.text

  while (this.peek().type === 'text') {
    body += '\n' + this.next().text
  }

  return this.inline.parse(body)
}

/**
 * Parse Current Token
 */

Parser.prototype.tok = function () {
  switch (this.token.type) {
    case 'space': {
      return {
        object: 'text',
        leaves: [
          {
            text: ''
          }
        ]
      }
    }
    case 'hr': {
      return this.renderer.hr()
    }
    case 'heading': {
      return this.renderer.heading(this.inline.parse(this.token.text), this.token.depth)
    }
    case 'code': {
      // Text inside of code blocks should not be parsed for marks
      return this.renderer.code(
        [
          {
            object: 'text',
            leaves: [{ text: this.token.text }]
          }
        ],
        this.token.lang
      )
    }
    case 'table': {
      const body = []
      let i, row, j

      // header
      const cells = []
      for (i = 0; i < this.token.header.length; i++) {
        cells.push(
          this.renderer.tablecell(this.inline.parse(this.token.header[i]), {
            header: true,
            align: this.token.align[i]
          })
        )
      }
      body.push(this.renderer.tablerow(cells))

      for (i = 0; i < this.token.cells.length; i++) {
        row = this.token.cells[i]

        const cells = []
        for (j = 0; j < row.length; j++) {
          cells.push(
            this.renderer.tablecell(this.inline.parse(row[j]), {
              header: false,
              align: this.token.align[j]
            })
          )
        }

        body.push(this.renderer.tablerow(cells))
      }
      return this.renderer.table(body)
    }
    case 'blockquote_start': {
      const body = []

      while (this.next().type !== 'blockquote_end') {
        body.push(this.token.type === 'text' ? this.renderer.paragraph(this.inline.parse(this.token.text)) : this.tok())
      }
      return this.renderer.blockquote(body)
    }
    case 'list_start': {
      const body = []
      const style = this.token.style

      while (this.next().type !== 'list_end') {
        body.push(this.tok())
      }

      return this.renderer.list(body, style)
    }
    case 'loose_item_start':
    case 'list_item_start': {
      const body = []
      const flags = { checked: this.token.checked }

      while (this.next().type !== 'list_item_end') {
        body.push(this.token.type === 'text' ? this.renderer.paragraph(this.inline.parse(this.token.text)) : this.tok())
      }

      return this.renderer.listitem(body, flags)
    }
    case 'paragraph': {
      return this.renderer.paragraph(this.inline.parse(this.token.text))
    }
    case 'text': {
      return this.renderer.text(this.parseText())
    }
    default: {
      return ''
    }
  }
}

/**
 * Helpers
 */

function replace(regex: any, options: any) {
  regex = regex.source
  // eslint-disable-next-line unicorn/prefer-default-parameters
  options = options || ''
  return function self(name: any, val: any) {
    if (!name) {
      return new RegExp(regex, options)
    }
    val = val.source || val
    val = val.replaceAll(/(^|[^[])\^/g, '$1')
    regex = regex.replace(name, val)
    return self
  }
}

const MarkdownParser = {
  parse(src: any, options?: any) {
    options = assign({}, defaults, options)
    let fragment

    try {
      fragment = Parser.parse(Lexer.parse(src, options), options)

      if (fragment.length === 0) {
        fragment = [
          {
            object: 'block',
            type: 'paragraph',
            isVoid: false,
            data: {},
            nodes: EMPTY_PARAGRAPH_NODES
          }
        ]
      }
    } catch (error) {
      if (options.silent) {
        fragment = [
          {
            object: 'block',
            type: 'paragraph',
            isVoid: false,
            data: {},
            nodes: [
              {
                object: 'text',
                leaves: [
                  {
                    object: 'leaf',
                    text: 'An error occured:',
                    marks: []
                  },
                  {
                    object: 'leaf',
                    // @ts-expect-error
                    text: error.message,
                    marks: []
                  }
                ]
              }
            ]
          }
        ]
      } else {
        throw error
      }
    }

    return { nodes: fragment }
  }
}

function splitCells(tableRow: any) {
  // We must account for escaped pipes within the cell content
  const cells = tableRow.split(/[^\\]\| *?|^\|/)

  for (let i = 0; i < cells.length; i++) {
    cells[i] = cells[i].replaceAll('\\|', '|').trim()
  }

  return cells
}

export default MarkdownParser
